import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useLanguage } from '@/state/language/LanguageProvider';
import { ADMIN_BILLING_TABS, type AdminBillingTabId } from './AdminBilling.tabs';
import { AdminBillingProviderConfigurationTab } from './tabs/AdminBillingProviderConfigurationTab';
import {
  PencilSquareIcon,
  EyeIcon,
  EyeSlashIcon,
  ArchiveBoxXMarkIcon,
  CheckIcon,
  XMarkIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import api from '@/services/api';

interface BillingProvider {
  id: string;
  providerKey: string;
  environment: string;
  isActive: boolean;
  configJson: Record<string, unknown>;
}

interface ProvidersResponse {
  providers: BillingProvider[];
}

type BillingPeriod = 'monthly' | 'annual';

interface BillingPlan {
  id: string;
  aiTierId: string;
  providerId: string | null;
  planCode: string;
  name: string;
  subtitle?: string | null;
  billingPeriod: BillingPeriod;
  priceUsd: number;
  currency: string;
  externalProductId: string | null;
  externalPriceId: string | null;
  isActive: boolean;
  isRetired: boolean;
  isListed: boolean;
  createdAt: string;
  updatedAt: string;
}

/** Group of plans that share the same product (name, planCode, aiTierId, currency) — one row per price. */
interface PlanGroup {
  name: string;
  subtitle: string | null;
  planCode: string;
  aiTierId: string;
  currency: string;
  externalProductId: string | null;
  prices: BillingPlan[];
}

interface PlansResponse {
  plans: BillingPlan[];
}

interface StripeCatalogPrice {
  externalPriceId: string;
  amountCents: number;
  currency: string;
  interval: 'month' | 'year';
}

interface StripeCatalogProduct {
  externalProductId: string;
  name: string;
  description?: string | null;
  prices: StripeCatalogPrice[];
}

interface StripeCatalogResponse {
  catalog: StripeCatalogProduct[];
  message?: string;
}

interface AiTierSummary {
  id: string;
  name: string;
  dailyTokenCap: number;
  isSystem: boolean;
}

interface TiersResponse {
  tiers: AiTierSummary[];
}

interface BillingCapability {
  id: string;
  code: string;
  displayName: string;
  description: string | null;
  isActive: boolean;
  createdAt?: string;
  updatedAt?: string;
}

interface BillingPromotion {
  id: string;
  code: string;
  name: string;
  description: string | null;
  discountType: 'percent' | 'amount';
  discountValue: number;
  durationType: 'once' | 'repeating' | 'forever';
  durationInCycles: number | null;
  startsAt: string | null;
  endsAt: string | null;
  isActive: boolean;
  externalPromotionId: string | null;
  createdAt: string;
  updatedAt: string;
}

interface BillingMetrics {
  mrr: number;
  arr: number;
  planMix: Array<{
    planId: string;
    planName: string;
    planCode: string;
    billingPeriod: string;
    count: number;
    monthlyRevenue: number;
  }>;
}

interface BillingGrowthReportPoint {
  bucketStart: string;
  newUsers: number;
  activeUsers: number;
  payingTenants: number;
  revenueUsd: number;
  taxUsd: number;
}

interface BillingGrowthReportResponse {
  start: string;
  end: string;
  granularity: 'day' | 'month';
  points: BillingGrowthReportPoint[];
}

interface BillingSignupCohortRow {
  cohortMonth: string;
  cohortSize: number;
  activeUsersByMonthOffset: number[];
}

interface BillingSignupCohortsResponse {
  startMonth: string;
  endMonth: string;
  maxOffset: number;
  cohorts: BillingSignupCohortRow[];
}

interface BillingCapabilityWithScreens extends BillingCapability {
  screenIds: string[];
}

interface NavigationScreenOption {
  id: string;
  screenCode: string;
  displayName: string;
}

interface RoleForExempt {
  roleName: string;
  displayName: string;
}

/** Fallback when the API returns no roles so the exempt list is always configurable. */
const DEFAULT_ROLES_FOR_EXEMPT: RoleForExempt[] = [
  { roleName: 'Platform Administrator', displayName: 'Platform Administrator' },
  { roleName: 'Tenant Administrator', displayName: 'Tenant Administrator' },
  { roleName: 'General User', displayName: 'General User' },
];

const makePlanCode = (name: string): string => {
  const base = name && name.trim().length > 0 ? name.trim() : 'plan';
  const slug = base
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || 'plan';
};

const defaultConfig = {
  publishableKey: '',
  secretKey: '',
  webhookSecret: '',
};

const AdminBillingPage: React.FC = () => {
  const { t } = useLanguage();
  const [providers, setProviders] = useState<BillingProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [testingConnection, setTestingConnection] = useState(false);

  const [plans, setPlans] = useState<BillingPlan[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [tiers, setTiers] = useState<AiTierSummary[]>([]);
  const [stripeCatalog, setStripeCatalog] = useState<StripeCatalogProduct[]>([]);
  const [stripeCatalogLoading, setStripeCatalogLoading] = useState(false);

  const [planModalOpen, setPlanModalOpen] = useState(false);
  const [planSaving, setPlanSaving] = useState(false);
  const [planForm, setPlanForm] = useState<{
    idMonthly?: string;
    idAnnual?: string;
    aiTierId: string;
    planCode: string;
    name: string;
    subtitle: string;
    monthlyPriceUsd: string;
    annualPriceUsd: string;
    currency: string;
    isActive: boolean;
    selectedCapabilityIds: string[];
  }>({
    aiTierId: '',
    planCode: '',
    name: '',
    subtitle: '',
    monthlyPriceUsd: '',
    annualPriceUsd: '',
    currency: 'usd',
    isActive: true,
    selectedCapabilityIds: [],
  });

  const [capabilities, setCapabilities] = useState<BillingCapability[]>([]);
  const [capabilitiesLoading, setCapabilitiesLoading] = useState(false);
  const [capabilityModalOpen, setCapabilityModalOpen] = useState(false);
  const [capabilitySaving, setCapabilitySaving] = useState(false);
  const [capabilityForm, setCapabilityForm] = useState<{
    id?: string;
    code: string;
    displayName: string;
    description: string;
    isActive: boolean;
    selectedScreenIds: string[];
  }>({
    code: '',
    displayName: '',
    description: '',
    isActive: true,
    selectedScreenIds: [],
  });
  const [capabilityScreenSearch, setCapabilityScreenSearch] = useState('');
  const [screens, setScreens] = useState<NavigationScreenOption[]>([]);

  const [rolesForExempt, setRolesForExempt] = useState<RoleForExempt[]>([]);
  const [exemptRoleNames, setExemptRoleNames] = useState<string[]>([]);
  const [exemptRolesSaving, setExemptRolesSaving] = useState(false);

  const [promotions, setPromotions] = useState<BillingPromotion[]>([]);
  const [promotionsLoading, setPromotionsLoading] = useState(false);
  const [promotionModalOpen, setPromotionModalOpen] = useState(false);
  const [promotionSaving, setPromotionSaving] = useState(false);
  const [promotionForm, setPromotionForm] = useState<{
    id?: string;
    code: string;
    name: string;
    description: string;
    discountType: 'percent' | 'amount';
    discountValue: string;
    durationType: 'once' | 'repeating' | 'forever';
    durationInCycles: string;
    startsAt: string;
    endsAt: string;
    isActive: boolean;
    selectedPlanIds: string[];
  }>({
    code: '',
    name: '',
    description: '',
    discountType: 'percent',
    discountValue: '',
    durationType: 'once',
    durationInCycles: '',
    startsAt: '',
    endsAt: '',
    isActive: true,
    selectedPlanIds: [],
  });

  const [metrics, setMetrics] = useState<BillingMetrics | null>(null);
  const [metricsLoading, setMetricsLoading] = useState(false);

  const [growthReport, setGrowthReport] = useState<BillingGrowthReportResponse | null>(null);
  const [growthLoading, setGrowthLoading] = useState(false);

  const [signupCohorts, setSignupCohorts] = useState<BillingSignupCohortsResponse | null>(null);
  const [cohortsLoading, setCohortsLoading] = useState(false);

  const [form, setForm] = useState<{
    providerKey: string;
    environment: string;
    isActive: boolean;
    publishableKey: string;
    secretKey: string;
    webhookSecret: string;
  }>({
    providerKey: 'stripe',
    environment: 'test',
    isActive: false,
    publishableKey: '',
    secretKey: '',
    webhookSecret: '',
  });
  const [showProviderModal, setShowProviderModal] = useState(false);
  const [retireConfirmPlanId, setRetireConfirmPlanId] = useState<string | null>(null);
  const [deleteConfirmProductId, setDeleteConfirmProductId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<AdminBillingTabId>('providerConfiguration');

  const loadProviders = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await api.get<ProvidersResponse>('/ai/admin/billing/providers');
      const list = res.data?.providers ?? [];
      setProviders(list);

      const stripeTest = list.find(
        (p) => p.providerKey === 'stripe' && p.environment === 'test'
      );
      if (stripeTest) {
        const cfg = {
          ...defaultConfig,
          ...(stripeTest.configJson || {}),
        } as typeof defaultConfig;
        setForm({
          providerKey: stripeTest.providerKey,
          environment: stripeTest.environment,
          isActive: stripeTest.isActive,
          publishableKey: cfg.publishableKey || '',
          secretKey: cfg.secretKey ? '••••••••' : '',
          webhookSecret: cfg.webhookSecret ? '••••••••' : '',
        });
      } else {
        setForm((prev) => ({
          ...prev,
          providerKey: 'stripe',
          environment: 'test',
        }));
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string; error?: string }; status?: number } };
      const msg =
        err.response?.status === 403
          ? 'Platform Administrator access required'
          : err.response?.data?.message ||
            err.response?.data?.error ||
            (e instanceof Error ? e.message : 'Failed to load billing providers');
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const loadPlans = async () => {
    try {
      setPlansLoading(true);
      const res = await api.get<PlansResponse>('/ai/admin/billing/plans');
      setPlans(res.data?.plans ?? []);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string; error?: string } } };
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        (e instanceof Error ? e.message : 'Failed to load billing plans');
      setError((prev) => prev ?? msg);
      toast.error(msg);
    } finally {
      setPlansLoading(false);
    }
  };

  const loadStripeCatalog = async () => {
    try {
      setStripeCatalogLoading(true);
      const res = await api.get<StripeCatalogResponse>('/ai/admin/billing/stripe-catalog');
      setStripeCatalog(res.data?.catalog ?? []);
    } catch {
      setStripeCatalog([]);
    } finally {
      setStripeCatalogLoading(false);
    }
  };

  const loadTiers = async () => {
    try {
      const res = await api.get<TiersResponse>('/ai/admin/tiers');
      setTiers(res.data?.tiers ?? []);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string; error?: string } } };
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        (e instanceof Error ? e.message : 'Failed to load AI tiers');
      setError((prev) => prev ?? msg);
      toast.error(msg);
    }
  };

  const loadCapabilities = async () => {
    try {
      setCapabilitiesLoading(true);
      const res = await api.get<{ capabilities: BillingCapability[] }>('/ai/admin/billing/capabilities');
      setCapabilities(res.data?.capabilities ?? []);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string; error?: string } } };
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        (e instanceof Error ? e.message : 'Failed to load capabilities');
      setError((prev) => prev ?? msg);
      toast.error(msg);
    } finally {
      setCapabilitiesLoading(false);
    }
  };

  const loadScreens = async () => {
    try {
      const res = await api.get<{ screens: NavigationScreenOption[] }>('/ai/admin/billing/screens');
      setScreens(res.data?.screens ?? []);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string; error?: string } } };
      toast.error(err.response?.data?.message || 'Failed to load screens');
    }
  };

  const loadExemptRoles = async () => {
    try {
      const res = await api.get<{ exemptRoleNames: string[] }>('/ai/admin/billing/capabilities/exempt-roles');
      setExemptRoleNames(res.data?.exemptRoleNames ?? []);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || 'Failed to load exempt roles');
    }
  };

  const loadRolesForExempt = async () => {
    try {
      const res = await api.get<{ roles: RoleForExempt[] }>('/ai/admin/billing/capabilities/roles-for-exempt');
      const roles = res.data?.roles ?? [];
      setRolesForExempt(roles.length > 0 ? roles : DEFAULT_ROLES_FOR_EXEMPT);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || 'Failed to load roles');
      setRolesForExempt(DEFAULT_ROLES_FOR_EXEMPT);
    }
  };

  const loadPromotions = async () => {
    try {
      setPromotionsLoading(true);
      const res = await api.get<{ promotions: BillingPromotion[] }>('/ai/admin/billing/promotions');
      setPromotions(res.data?.promotions ?? []);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || 'Failed to load promotions');
    } finally {
      setPromotionsLoading(false);
    }
  };

  const loadMetrics = async () => {
    try {
      setMetricsLoading(true);
      const res = await api.get<BillingMetrics>('/ai/admin/billing/metrics');
      setMetrics(res.data ?? null);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || 'Failed to load metrics');
      setMetrics(null);
    } finally {
      setMetricsLoading(false);
    }
  };

  const loadGrowthReport = async () => {
    try {
      setGrowthLoading(true);
      const start = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString();
      const end = new Date().toISOString();
      const res = await api.get<BillingGrowthReportResponse>('/ai/admin/billing/reports/growth', {
        params: { start, end, granularity: 'day' },
      });
      setGrowthReport(res.data ?? null);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || 'Failed to load growth report');
      setGrowthReport(null);
    } finally {
      setGrowthLoading(false);
    }
  };

  const loadSignupCohorts = async () => {
    try {
      setCohortsLoading(true);
      const startMonth = new Date(new Date().getFullYear(), new Date().getMonth() - 12, 1).toISOString();
      const endMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString();
      const res = await api.get<BillingSignupCohortsResponse>('/ai/admin/billing/reports/cohorts/signup', {
        params: { startMonth, endMonth, maxOffset: 6 },
      });
      setSignupCohorts(res.data ?? null);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || 'Failed to load signup cohorts');
      setSignupCohorts(null);
    } finally {
      setCohortsLoading(false);
    }
  };

  const handleSaveExemptRoles = async () => {
    try {
      setExemptRolesSaving(true);
      setError(null);
      await api.put('/ai/admin/billing/capabilities/exempt-roles', {
        exemptRoleNames,
      });
      toast.success('Exempt roles saved. These roles will not be gated by capabilities (RBAC only).');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string; error?: string } } };
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        (e instanceof Error ? e.message : 'Failed to save exempt roles');
      setError(msg);
      toast.error(msg);
    } finally {
      setExemptRolesSaving(false);
    }
  };

  useEffect(() => {
    const run = async () => {
      await Promise.all([
        loadProviders(),
        loadTiers(),
        loadPlans(),
        loadStripeCatalog(),
        loadCapabilities(),
        loadScreens(),
        loadExemptRoles(),
        loadRolesForExempt(),
        loadPromotions(),
        loadMetrics(),
        loadGrowthReport(),
        loadSignupCohorts(),
      ]);
      // Ensure a default tier is selected for new plan form once tiers are loaded
      setPlanForm((prev) => ({
        ...prev,
        aiTierId: prev.aiTierId || (tiers[0]?.id ?? ''),
      }));
    };
    void run();
  }, []);

  const { activePlanGroups, retiredPlanGroups } = useMemo(() => {
    const buildGroups = (input: BillingPlan[]): PlanGroup[] => {
      const map = new Map<string, BillingPlan[]>();
      for (const p of input) {
        const key = `${p.name}|${p.planCode}|${p.aiTierId}|${p.currency}`;
        if (!map.has(key)) map.set(key, []);
        map.get(key)!.push(p);
      }
      return Array.from(map.entries()).map(([, group]): PlanGroup => ({
        name: group[0].name,
        subtitle: group[0].subtitle ?? null,
        planCode: group[0].planCode,
        aiTierId: group[0].aiTierId,
        currency: group[0].currency,
        externalProductId: group[0].externalProductId,
        prices: group.sort((a, b) =>
          a.billingPeriod === 'monthly' && b.billingPeriod !== 'monthly'
            ? -1
            : a.billingPeriod !== 'monthly' && b.billingPeriod === 'monthly'
              ? 1
              : 0
        ),
      }));
    };

    const active = plans.filter((p) => !p.isRetired);
    const retired = plans.filter((p) => p.isRetired);

    return {
      activePlanGroups: buildGroups(active),
      retiredPlanGroups: buildGroups(retired),
    };
  }, [plans]);

  const handleRetirePlan = async (planId: string) => {
    try {
      setError(null);
      await api.post(`/ai/admin/billing/plans/${planId}/retire`);
      await loadPlans();
      toast.success('Plan retired. It will no longer be offered for new subscriptions.');
      setRetireConfirmPlanId(null);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string; error?: string } } };
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        (e instanceof Error ? e.message : 'Failed to retire plan');
      setError(msg);
      toast.error(msg);
    }
  };

  const handleSetPlanVisibility = async (planId: string, isListed: boolean) => {
    try {
      setError(null);
      await api.put(`/ai/admin/billing/plans/${planId}/visibility`, { isListed });
      await loadPlans();
      toast.success(isListed ? 'Plan is now visible in the catalog.' : 'Plan is now hidden from the catalog.');
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string; error?: string } } };
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        (e instanceof Error ? e.message : 'Failed to update visibility');
      setError(msg);
      toast.error(msg);
    }
  };

  const handleDeleteProduct = async (externalProductId: string) => {
    try {
      setError(null);
      const res = await api.delete<{ providerWarning?: string | null }>(
        `/ai/admin/billing/products/${externalProductId}`
      );
      await loadPlans();
      await loadStripeCatalog();
      if (res.data?.providerWarning) {
        toast.error(res.data.providerWarning);
      }
      toast.success('Product deleted (archived) and plans retired.');
      setDeleteConfirmProductId(null);
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string; error?: string } } };
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        (e instanceof Error ? e.message : 'Failed to delete product');
      setError(msg);
      toast.error(msg);
    }
  };

  const renderPlanGroups = (groups: PlanGroup[], opts: { section: 'active' | 'retired' }) => {
    if (groups.length === 0) return null;
    return (
      <div className="space-y-6">
        {groups.map((group) => {
          const tierName = tiers.find((t) => t.id === group.aiTierId)?.name ?? 'Unknown tier';
          const canDeleteProduct = opts.section === 'active' && !!group.externalProductId;

          return (
            <div
              key={`${opts.section}-${group.name}-${group.planCode}-${group.aiTierId}`}
              className={
                opts.section === 'retired'
                  ? 'border border-gray-200 rounded-md overflow-hidden opacity-80'
                  : 'border border-gray-200 rounded-md overflow-hidden'
              }
            >
              <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-start justify-between gap-2">
                <div>
                  <div className="font-medium text-gray-900">{group.name}</div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {group.planCode} · {tierName} · {group.currency.toUpperCase()}
                  </div>
                  {group.externalProductId && (
                    <div className="text-xs text-gray-500 font-mono mt-1">
                      Stripe product: {group.externalProductId}
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-1 shrink-0">
                  {canDeleteProduct &&
                    group.externalProductId &&
                    (deleteConfirmProductId === group.externalProductId ? (
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          onClick={() => setDeleteConfirmProductId(null)}
                          className="px-2 py-1 rounded text-xs text-gray-700 bg-white border border-gray-200 hover:bg-gray-50"
                          title="Cancel delete"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          onClick={() => void handleDeleteProduct(group.externalProductId!)}
                          className="px-2 py-1 rounded text-xs text-white bg-[var(--color-danger)] hover:opacity-90"
                          title="Confirm delete product"
                        >
                          Delete
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setDeleteConfirmProductId(group.externalProductId!)}
                        className="p-1.5 rounded text-gray-500 hover:text-[var(--color-danger)] hover:bg-[rgba(239,68,68,0.10)]"
                        title="Delete Stripe product and retire all prices"
                        aria-label="Delete Stripe product and retire all prices"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    ))}

                  <button
                    type="button"
                    onClick={() => openEditPlanModal(group)}
                    className="p-1.5 rounded text-gray-500 hover:text-[var(--color-primary-text)] hover:bg-[var(--color-hover-bg)]"
                    title="Edit plan (product + prices)"
                    aria-label="Edit plan (product + prices)"
                  >
                    <PencilSquareIcon className="h-5 w-5" />
                  </button>
                </div>
              </div>

              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Period
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Price
                    </th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                      Active
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Status
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Sync
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {group.prices.map((plan) => {
                    const isRetired = plan.isRetired === true;
                    const isHidden = plan.isListed === false;
                    const showRetireConfirm = retireConfirmPlanId === plan.id;
                    const synced =
                      opts.section === 'active' &&
                      !isRetired &&
                      !!plan.externalProductId &&
                      !!plan.externalPriceId;

                    return (
                      <tr key={plan.id}>
                        <td className="px-4 py-2 text-sm text-gray-700 capitalize">{plan.billingPeriod}</td>
                        <td className="px-4 py-2 text-sm text-gray-900">
                          {plan.priceUsd.toFixed(2)} {plan.currency.toUpperCase()}
                          {plan.externalPriceId && (
                            <div className="text-xs text-gray-500 font-mono mt-0.5">
                              {plan.externalPriceId}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-2 text-sm text-center">
                          <span
                            className={
                              plan.isActive
                                ? 'inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700'
                                : 'inline-flex items-center rounded-full bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-600'
                            }
                          >
                            {plan.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-700">
                          {isRetired && (
                            <span className="inline-flex items-center rounded-full bg-[rgba(239,68,68,0.10)] px-2 py-0.5 text-xs font-medium text-[var(--color-danger)] mr-1">
                              Retired
                            </span>
                          )}
                          {isHidden && !isRetired && (
                            <span className="inline-flex items-center rounded-full bg-amber-50 px-2 py-0.5 text-xs font-medium text-amber-700">
                              Hidden
                            </span>
                          )}
                          {!isRetired && !isHidden && plan.isActive && (
                            <span className="text-xs text-gray-500">Visible</span>
                          )}
                          {!isRetired && !isHidden && !plan.isActive && (
                            <span className="inline-flex items-center rounded-full bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-700">
                              Paused
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-sm text-gray-700">
                          {opts.section === 'retired' || isRetired ? (
                            <span className="text-xs text-gray-400">—</span>
                          ) : synced ? (
                            <span className="inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
                              Synced
                            </span>
                          ) : (
                            <span className="inline-flex items-center rounded-full bg-yellow-50 px-2 py-0.5 text-xs font-medium text-yellow-700">
                              Not yet synced
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-sm text-right">
                          <div className="flex items-center justify-end gap-1">
                            {!isRetired && opts.section === 'active' && (
                              <>
                                <button
                                  type="button"
                                  onClick={() => openEditPlanModal(group)}
                                  className="p-1.5 rounded text-gray-500 hover:text-[var(--color-primary-text)] hover:bg-[var(--color-hover-bg)]"
                                  title="Edit plan (product + prices)"
                                  aria-label="Edit plan (product + prices)"
                                >
                                  <PencilSquareIcon className="h-5 w-5" />
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleSetPlanVisibility(plan.id, isHidden)}
                                  className="p-1.5 rounded text-gray-500 hover:text-[var(--color-primary-text)] hover:bg-[var(--color-hover-bg)]"
                                  title={isHidden ? 'Show plan' : 'Hide plan'}
                                  aria-label={isHidden ? 'Show plan' : 'Hide plan'}
                                >
                                  {isHidden ? <EyeIcon className="h-5 w-5" /> : <EyeSlashIcon className="h-5 w-5" />}
                                </button>
                                {showRetireConfirm ? (
                                  <>
                                    <button
                                      type="button"
                                      onClick={() => handleRetirePlan(plan.id)}
                                      className="p-1.5 rounded text-[var(--color-danger)] hover:bg-[rgba(220,38,38,0.08)]"
                                      title="Confirm retire"
                                      aria-label="Confirm retire"
                                    >
                                      <CheckIcon className="h-5 w-5" />
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setRetireConfirmPlanId(null)}
                                      className="p-1.5 rounded text-gray-500 hover:bg-[var(--color-hover-bg)]"
                                      title="Cancel"
                                      aria-label="Cancel"
                                    >
                                      <XMarkIcon className="h-5 w-5" />
                                    </button>
                                  </>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => setRetireConfirmPlanId(plan.id)}
                                    className="p-1.5 rounded text-gray-500 hover:text-[var(--color-danger)] hover:bg-[rgba(220,38,38,0.08)]"
                                    title="Retire plan"
                                    aria-label="Retire plan"
                                  >
                                    <ArchiveBoxXMarkIcon className="h-5 w-5" />
                                  </button>
                                )}
                              </>
                            )}
                            {(isRetired || opts.section === 'retired') && (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          );
        })}
      </div>
    );
  };

  // Editing is now handled via the same "plan modal" used for creation.

  const openNewPromotionModal = () => {
    setPromotionForm({
      code: '',
      name: '',
      description: '',
      discountType: 'percent',
      discountValue: '',
      durationType: 'once',
      durationInCycles: '',
      startsAt: '',
      endsAt: '',
      isActive: true,
      selectedPlanIds: [],
    });
    setPromotionModalOpen(true);
  };

  const openEditPromotionModal = async (promo: BillingPromotion) => {
    setPromotionForm({
      id: promo.id,
      code: promo.code,
      name: promo.name,
      description: promo.description ?? '',
      discountType: promo.discountType,
      discountValue: String(promo.discountValue),
      durationType: promo.durationType,
      durationInCycles: promo.durationInCycles != null ? String(promo.durationInCycles) : '',
      startsAt: promo.startsAt ? promo.startsAt.slice(0, 16) : '',
      endsAt: promo.endsAt ? promo.endsAt.slice(0, 16) : '',
      isActive: promo.isActive,
      selectedPlanIds: [],
    });
    try {
      const res = await api.get<{ planIds: string[] }>(`/ai/admin/billing/promotions/${promo.id}/plans`);
      setPromotionForm((prev) => ({ ...prev, selectedPlanIds: res.data?.planIds ?? [] }));
    } catch {
      setPromotionForm((prev) => ({ ...prev, selectedPlanIds: [] }));
    }
    setPromotionModalOpen(true);
  };

  const handleSavePromotion = async (): Promise<boolean> => {
    const discountNum = Number(promotionForm.discountValue);
    if (!Number.isFinite(discountNum) || discountNum < 0) {
      toast.error('Discount value must be a non-negative number.');
      return false;
    }
    if (promotionForm.durationType === 'repeating' && (!promotionForm.durationInCycles || Number(promotionForm.durationInCycles) < 1)) {
      toast.error('Duration in cycles is required when duration is repeating.');
      return false;
    }
    try {
      setPromotionSaving(true);
      setError(null);
      const saved = await api.post<BillingPromotion>('/ai/admin/billing/promotions', {
        id: promotionForm.id,
        code: promotionForm.code.trim(),
        name: promotionForm.name.trim(),
        description: promotionForm.description.trim() || undefined,
        discountType: promotionForm.discountType,
        discountValue: discountNum,
        durationType: promotionForm.durationType,
        durationInCycles:
          promotionForm.durationType === 'repeating' && promotionForm.durationInCycles
            ? parseInt(promotionForm.durationInCycles, 10)
            : null,
        startsAt: promotionForm.startsAt ? new Date(promotionForm.startsAt).toISOString() : null,
        endsAt: promotionForm.endsAt ? new Date(promotionForm.endsAt).toISOString() : null,
        isActive: promotionForm.isActive,
      });
      const promoId = saved.data?.id ?? promotionForm.id;
      if (promoId) {
        await api.put(`/ai/admin/billing/promotions/${promoId}/plans`, {
          planIds: promotionForm.selectedPlanIds,
        });
      }
      await loadPromotions();
      await loadMetrics();
      toast.success('Promotion saved.');
      setPromotionModalOpen(false);
      return true;
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string; error?: string } } };
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        (e instanceof Error ? e.message : 'Failed to save promotion');
      setError(msg);
      toast.error(msg);
      return false;
    } finally {
      setPromotionSaving(false);
    }
  };

  const openNewPlanModal = () => {
    setPlanForm({
      idMonthly: undefined,
      idAnnual: undefined,
      aiTierId: tiers[0]?.id ?? '',
      planCode: '',
      name: '',
      subtitle: '',
      monthlyPriceUsd: '',
      annualPriceUsd: '',
      currency: 'usd',
      isActive: true,
      selectedCapabilityIds: [],
    });
    setPlanModalOpen(true);
  };

  const openEditPlanModal = (group: PlanGroup) => {
    const monthly = group.prices.find((p) => p.billingPeriod === 'monthly');
    const annual = group.prices.find((p) => p.billingPeriod === 'annual');
    setPlanForm({
      idMonthly: monthly?.id,
      idAnnual: annual?.id,
      aiTierId: group.aiTierId,
      planCode: group.planCode,
      name: group.name,
      subtitle: group.subtitle ?? '',
      monthlyPriceUsd: monthly ? String(monthly.priceUsd) : '',
      annualPriceUsd: annual ? String(annual.priceUsd) : '',
      currency: group.currency,
      // Use monthly's isActive as the group-level toggle (both will be set to this on save).
      isActive: (monthly ?? annual)?.isActive ?? true,
      selectedCapabilityIds: [],
    });
    // Load capabilities selection from any plan in the group.
    void (async () => {
      try {
        const planIdForCaps = (monthly ?? annual)?.id;
        if (!planIdForCaps) return;
        const caps = await api.get<{ capabilityIds: string[] }>(
          `/ai/admin/billing/plans/${planIdForCaps}/capabilities`
        );
        setPlanForm((prev) => ({ ...prev, selectedCapabilityIds: caps.data?.capabilityIds ?? [] }));
      } catch {
        // best-effort; keep empty if fetch fails
      }
    })();

    setPlanModalOpen(true);
  };

  const handleSavePlan = async (): Promise<boolean> => {
    try {
      setPlanSaving(true);
      setError(null);

      if (!planForm.aiTierId) {
        toast.error('AI tier is required');
        return false;
      }
      if (!planForm.name.trim()) {
        toast.error('Plan name is required');
        return false;
      }
      const isNewGroup = !planForm.idMonthly && !planForm.idAnnual;
      const monthlyStr = planForm.monthlyPriceUsd.trim();
      const annualStr = planForm.annualPriceUsd.trim();
      const monthlyPrice = monthlyStr ? Number(monthlyStr) : NaN;
      const annualPrice = annualStr ? Number(annualStr) : NaN;

      // System-of-record model: a plan group is one product with two prices (monthly + annual).
      // - New group requires both prices.
      // - Edit allows leaving a price blank to keep the existing value (if that price already exists).
      if (isNewGroup) {
        if (!Number.isFinite(monthlyPrice) || monthlyPrice < 0) {
          toast.error('Monthly price is required and must be a non-negative number.');
          return false;
        }
        if (!Number.isFinite(annualPrice) || annualPrice < 0) {
          toast.error('Annual price is required and must be a non-negative number.');
          return false;
        }
      } else {
        if (monthlyStr && (!Number.isFinite(monthlyPrice) || monthlyPrice < 0)) {
          toast.error('Monthly price must be a non-negative number.');
          return false;
        }
        if (annualStr && (!Number.isFinite(annualPrice) || annualPrice < 0)) {
          toast.error('Annual price must be a non-negative number.');
          return false;
        }
        // If editing and the price field is empty, we won't send an update for that period.
      }

      // If admin doesn't provide a plan code, generate a unique one.
      // This avoids collisions with legacy DB uniqueness constraints while ensuring monthly+annual share the same product/group.
      let effectivePlanCode = planForm.planCode.trim();
      if (!effectivePlanCode) {
        const base = makePlanCode(planForm.name);
        const suffix = Date.now().toString(36).slice(-6);
        effectivePlanCode = `${base}-${suffix}`;
      }

      const currency = planForm.currency.trim() || 'usd';
      const common = {
        aiTierId: planForm.aiTierId,
        planCode: effectivePlanCode,
        name: planForm.name.trim(),
        subtitle: planForm.subtitle.trim() || null,
        currency,
        isActive: planForm.isActive,
        isRetired: false,
      };

      // Save monthly first, then annual, so annual can reuse the same Stripe product (one product, two prices).
      const savedIds: string[] = [];
      if (
        (isNewGroup && Number.isFinite(monthlyPrice) && monthlyPrice >= 0) ||
        (!isNewGroup && monthlyStr)
      ) {
        if (!Number.isFinite(monthlyPrice) || monthlyPrice < 0) {
          toast.error('Monthly price must be a non-negative number.');
          return false;
        }
        const payloadMonthly = {
          id: planForm.idMonthly,
          ...common,
          billingPeriod: 'monthly' as BillingPeriod,
          priceUsd: monthlyPrice,
        };
        const res =
          planForm.idMonthly
            ? await api.put<BillingPlan>('/ai/admin/billing/plans', payloadMonthly)
            : await api.post<BillingPlan>('/ai/admin/billing/plans', payloadMonthly);
        // Backend may auto-adjust planCode to avoid legacy DB uniqueness collisions; use the returned planCode for the rest of the save.
        if (!planForm.idMonthly && res.data?.planCode) {
          effectivePlanCode = res.data.planCode;
        }
        if (res.data?.id) savedIds.push(res.data.id);
      }
      if (
        (isNewGroup && Number.isFinite(annualPrice) && annualPrice >= 0) ||
        (!isNewGroup && annualStr)
      ) {
        if (!Number.isFinite(annualPrice) || annualPrice < 0) {
          toast.error('Annual price must be a non-negative number.');
          return false;
        }
        const payloadAnnual = {
          id: planForm.idAnnual,
          ...common,
          planCode: effectivePlanCode,
          billingPeriod: 'annual' as BillingPeriod,
          priceUsd: annualPrice,
        };
        const res =
          planForm.idAnnual
            ? await api.put<BillingPlan>('/ai/admin/billing/plans', payloadAnnual)
            : await api.post<BillingPlan>('/ai/admin/billing/plans', payloadAnnual);
        if (res.data?.id) savedIds.push(res.data.id);
      }

      await loadPlans();
      loadStripeCatalog();

      const idsForCapabilities =
        savedIds.length > 0
          ? savedIds
          : (await api.get<PlansResponse>('/ai/admin/billing/plans')).data?.plans
              ?.filter(
                (p) =>
                  p.planCode === effectivePlanCode &&
                  p.aiTierId === planForm.aiTierId &&
                  p.currency === currency
              )
              .map((p) => p.id) ?? [];
      for (const planId of idsForCapabilities) {
        await api.put(`/ai/admin/billing/plans/${planId}/capabilities`, {
          capabilityIds: planForm.selectedCapabilityIds,
        });
      }

      toast.success('Billing plan saved.');
      return true;
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string; error?: string } } };
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        (e instanceof Error ? e.message : 'Failed to save billing plan');
      setError(msg);
      toast.error(msg);
      return false;
    } finally {
      setPlanSaving(false);
    }
  };

  const handleSave = async (): Promise<boolean> => {
    try {
      setSaving(true);
      setError(null);

      const configJson: Record<string, unknown> = {};
      if (form.publishableKey.trim()) configJson.publishableKey = form.publishableKey.trim();
      if (form.secretKey.trim() && form.secretKey.trim() !== '••••••••') {
        configJson.secretKey = form.secretKey.trim();
      }
      if (form.webhookSecret.trim() && form.webhookSecret.trim() !== '••••••••') {
        configJson.webhookSecret = form.webhookSecret.trim();
      }

      await api.put('/ai/admin/billing/providers', {
        providerKey: form.providerKey.trim(),
        environment: form.environment.trim(),
        configJson,
        isActive: form.isActive,
      });
      await loadProviders();
      toast.success('Billing provider saved.');
      return true;
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string; error?: string }; status?: number } };
      const msg =
        err.response?.status === 403
          ? 'Platform Administrator access required'
          : err.response?.data?.message ||
            err.response?.data?.error ||
            (e instanceof Error ? e.message : 'Failed to save provider');
      setError(msg);
      toast.error(msg);
      return false;
    } finally {
      setSaving(false);
    }
  };

  const openNewCapabilityModal = () => {
    setCapabilityForm({
      code: '',
      displayName: '',
      description: '',
      isActive: true,
      selectedScreenIds: [],
    });
    setCapabilityScreenSearch('');
    setCapabilityModalOpen(true);
  };

  const openEditCapabilityModal = async (cap: BillingCapability) => {
    try {
      const res = await api.get<BillingCapabilityWithScreens>(`/ai/admin/billing/capabilities/${cap.id}`);
      const data = res.data;
      if (data) {
        setCapabilityForm({
          id: data.id,
          code: data.code,
          displayName: data.displayName,
          description: data.description ?? '',
          isActive: data.isActive,
          selectedScreenIds: data.screenIds ?? [],
        });
        setCapabilityScreenSearch('');
        setCapabilityModalOpen(true);
      }
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err.response?.data?.message || 'Failed to load capability');
    }
  };

  const handleSaveCapability = async (): Promise<boolean> => {
    try {
      setCapabilitySaving(true);
      setError(null);
      if (!capabilityForm.code.trim()) {
        toast.error('Code is required');
        return false;
      }
      if (!capabilityForm.displayName.trim()) {
        toast.error('Display name is required');
        return false;
      }
      if (capabilityForm.id) {
        await api.put('/ai/admin/billing/capabilities', {
          id: capabilityForm.id,
          code: capabilityForm.code.trim(),
          displayName: capabilityForm.displayName.trim(),
          description: capabilityForm.description.trim() || null,
          isActive: capabilityForm.isActive,
        });
        await api.put(`/ai/admin/billing/capabilities/${capabilityForm.id}/screens`, {
          screenIds: capabilityForm.selectedScreenIds,
        });
      } else {
        const createRes = await api.post<BillingCapability>('/ai/admin/billing/capabilities', {
          code: capabilityForm.code.trim(),
          displayName: capabilityForm.displayName.trim(),
          description: capabilityForm.description.trim() || null,
          isActive: capabilityForm.isActive,
        });
        const newId = createRes.data?.id;
        if (newId && capabilityForm.selectedScreenIds.length > 0) {
          await api.put(`/ai/admin/billing/capabilities/${newId}/screens`, {
            screenIds: capabilityForm.selectedScreenIds,
          });
        }
      }
      await loadCapabilities();
      toast.success('Capability saved.');
      setCapabilityModalOpen(false);
      return true;
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string; error?: string } } };
      const msg =
        err.response?.data?.message ||
        err.response?.data?.error ||
        (e instanceof Error ? e.message : 'Failed to save capability');
      setError(msg);
      toast.error(msg);
      return false;
    } finally {
      setCapabilitySaving(false);
    }
  };

  // Provider connection testing lives in Provider Configuration tab.

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--color-primary-text)] mx-auto" />
            <p className="mt-4 text-gray-600 text-sm">
              {t('adminBilling.loadingConfig') || 'Loading billing configuration…'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center gap-4">
            <Link
              to="/admin/dashboard"
              className="text-[var(--color-info)] hover:underline font-medium"
            >
              {t('adminBilling.breadcrumb.admin') || '← Admin'}
            </Link>
            <div>
              <h1 className="text-2xl font-bold text-[var(--color-surface-text)]">
                {t('adminBilling.title') || 'Billing providers'}
              </h1>
              <p className="text-sm text-gray-600 mt-1">
                {t('adminBilling.subtitle') ||
                  'Configure payment processors (e.g. Stripe) used for TMOS subscriptions and billing.'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        {error && (
          <div className="bg-[rgba(239,68,68,0.10)] border border-[var(--color-danger)] rounded-md p-4 text-sm text-[var(--color-danger)]">
            {error}
          </div>
        )}

        <div className="bg-white shadow rounded-lg px-3 py-2">
          <div className="flex flex-wrap gap-2">
            {ADMIN_BILLING_TABS.map((tab) => {
              const selected = tab.id === activeTab;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={
                    selected
                      ? 'px-3 py-1.5 text-sm rounded-md bg-[var(--color-primary-text)] text-white hover:opacity-90'
                      : 'px-3 py-1.5 text-sm rounded-md bg-[var(--color-secondary-bg)] text-[var(--color-tab-inactive-text)] hover:bg-[var(--color-accent-bg)]'
                  }
                >
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {activeTab === 'providerConfiguration' && (
          <>
            <AdminBillingProviderConfigurationTab
              providers={providers}
              saving={saving}
              setSaving={setSaving}
              testingConnection={testingConnection}
              setTestingConnection={setTestingConnection}
              error={error}
              setError={setError}
              loadProviders={loadProviders}
              openProviderModal={(nextForm) => {
                setForm(nextForm);
                setShowProviderModal(true);
              }}
            />
            {/* Stripe catalog: all active products and prices from Stripe (source of truth) */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">Stripe catalog</h2>
              <p className="text-sm text-gray-600 mb-4">
                Active products and prices from Stripe (source of truth).
              </p>
              {stripeCatalogLoading ? (
                <p className="text-sm text-gray-500">Loading Stripe catalog…</p>
              ) : stripeCatalog.length === 0 ? (
                <p className="text-sm text-gray-500">
                  No Stripe catalog (connect an active Stripe provider to see active products and prices).
                </p>
              ) : (
                <div className="border border-gray-200 rounded-md overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Product
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Product ID
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          Prices
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {stripeCatalog.map((product) => (
                        <tr key={product.externalProductId}>
                          <td className="px-4 py-2 text-sm text-gray-900">
                            <div className="font-medium">{product.name || '(no name)'}</div>
                            {product.description && (
                              <div className="text-xs text-gray-500">{product.description}</div>
                            )}
                          </td>
                          <td className="px-4 py-2 text-xs text-gray-500 font-mono">
                            {product.externalProductId}
                          </td>
                          <td className="px-4 py-2 text-sm text-gray-700">
                            {product.prices.length === 0 ? (
                              <span className="text-gray-400">No active prices</span>
                            ) : (
                              <ul className="list-disc list-inside space-y-0.5">
                                {product.prices.map((p) => (
                                  <li key={p.externalPriceId}>
                                    <span className="font-mono text-xs">{p.externalPriceId}</span>
                                    {' — '}
                                    {(p.amountCents / 100).toFixed(2)} {p.currency.toUpperCase()}/{p.interval}
                                  </li>
                                ))}
                              </ul>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </>
        )}

        {activeTab !== 'planCapabilities' && activeTab !== 'promotions' && activeTab !== 'analytics' && (
          <>
            {/* Placeholder so the existing sections keep rendering below in their original order */}
          </>
        )}

        {activeTab === 'analytics' && (
          <>
        {/* Metrics: MRR, ARR, plan mix */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">
            {t('adminBilling.metrics.title') || 'Billing metrics'}
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            {t('adminBilling.metrics.description') ||
              'Monthly and annual recurring revenue from active subscriptions; plan mix (count and revenue per plan).'}
          </p>
          {metricsLoading ? (
            <p className="text-sm text-gray-500">
              {t('adminBilling.metrics.loading') || 'Loading metrics…'}
            </p>
          ) : metrics ? (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-6">
                <div>
                  <span className="text-xs font-medium text-gray-500 uppercase">
                    {t('adminBilling.metrics.mrr') || 'MRR'}
                  </span>
                  <p className="text-xl font-semibold text-gray-900">
                    ${metrics.mrr.toFixed(2)}
                  </p>
                </div>
                <div>
                  <span className="text-xs font-medium text-gray-500 uppercase">
                    {t('adminBilling.metrics.arr') || 'ARR'}
                  </span>
                  <p className="text-xl font-semibold text-gray-900">
                    ${metrics.arr.toFixed(2)}
                  </p>
                </div>
              </div>
              {metrics.planMix.length > 0 ? (
                <div className="border border-gray-200 rounded-md overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          {t('adminBilling.metrics.table.plan') || 'Plan'}
                        </th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                          {t('adminBilling.metrics.table.period') || 'Period'}
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                          {t('adminBilling.metrics.table.subscriptions') || 'Subscriptions'}
                        </th>
                        <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                          {t('adminBilling.metrics.table.monthlyRevenue') || 'Monthly revenue'}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {metrics.planMix.map((row) => (
                        <tr key={`${row.planId}-${row.billingPeriod}`}>
                          <td className="px-4 py-2 text-sm text-gray-900">{row.planName}</td>
                          <td className="px-4 py-2 text-sm text-gray-700 capitalize">{row.billingPeriod}</td>
                          <td className="px-4 py-2 text-sm text-gray-700 text-right">{row.count}</td>
                          <td className="px-4 py-2 text-sm text-gray-700 text-right">${row.monthlyRevenue.toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-gray-500">
                  {t('adminBilling.metrics.noSubscriptions') || 'No active subscriptions yet.'}
                </p>
              )}
            </div>
          ) : (
            <p className="text-sm text-gray-500">
              {t('adminBilling.metrics.unableToLoad') || 'Unable to load metrics.'}
            </p>
          )}
        </div>

        {/* Analytics (Phase 6): Growth + cohorts */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Analytics (beta)</h2>
              <p className="text-sm text-gray-600 mt-1">
                Lightweight platform analytics from TMOS data (users, usage events, subscriptions, top-ups).
              </p>
            </div>
            <button
              type="button"
              onClick={() => void Promise.all([loadGrowthReport(), loadSignupCohorts()])}
              className="px-3 py-1.5 text-xs rounded-md bg-[var(--color-primary-text)] text-white hover:opacity-90"
            >
              Refresh
            </button>
          </div>

          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Growth (last 30 days)</h3>
            {growthLoading ? (
              <p className="text-sm text-gray-500">Loading growth…</p>
            ) : growthReport && growthReport.points?.length ? (
              <div className="border border-gray-200 rounded-md overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">New users</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Active users</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Paying tenants</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Revenue (USD)</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Tax (USD)</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {growthReport.points.map((p) => (
                      <tr key={p.bucketStart}>
                        <td className="px-4 py-2 text-sm text-gray-900">{p.bucketStart}</td>
                        <td className="px-4 py-2 text-sm text-gray-700 text-right">{p.newUsers}</td>
                        <td className="px-4 py-2 text-sm text-gray-700 text-right">{p.activeUsers}</td>
                        <td className="px-4 py-2 text-sm text-gray-700 text-right">{p.payingTenants}</td>
                        <td className="px-4 py-2 text-sm text-gray-700 text-right">${p.revenueUsd.toFixed(2)}</td>
                        <td className="px-4 py-2 text-sm text-gray-700 text-right">${p.taxUsd.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-500">No growth data for this window.</p>
            )}
          </div>

          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-2">Signup cohorts (last 12 months)</h3>
            {cohortsLoading ? (
              <p className="text-sm text-gray-500">Loading cohorts…</p>
            ) : signupCohorts && signupCohorts.cohorts?.length ? (
              <div className="border border-gray-200 rounded-md overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cohort month</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Size</th>
                      {signupCohorts.cohorts[0]?.activeUsersByMonthOffset?.map((_, idx) => (
                        <th
                          key={idx}
                          className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase"
                        >
                          M+{idx}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {signupCohorts.cohorts.map((c) => (
                      <tr key={c.cohortMonth}>
                        <td className="px-4 py-2 text-sm text-gray-900">{c.cohortMonth}</td>
                        <td className="px-4 py-2 text-sm text-gray-700 text-right">{c.cohortSize}</td>
                        {c.activeUsersByMonthOffset.map((v, idx) => (
                          <td key={idx} className="px-4 py-2 text-sm text-gray-700 text-right">
                            {v}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-gray-500">No cohort data.</p>
            )}
          </div>
        </div>
          </>
        )}

        {activeTab === 'planCapabilities' && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Capabilities (plan entitlements)
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Define business-level capabilities and which navigation screens they include. Attach capabilities to plans so tenants get the right feature access.
              </p>
            </div>
            <button
              type="button"
              onClick={openNewCapabilityModal}
              className="px-3 py-1.5 text-xs rounded-md bg-[var(--color-primary-text)] text-white hover:opacity-90"
            >
              Add capability
            </button>
          </div>

          <div className="mb-6 p-4 bg-gray-50 rounded-md border border-gray-200">
            <h3 className="text-sm font-semibold text-gray-900 mb-1">
              Roles exempt from capability gating
            </h3>
            <p className="text-xs text-gray-600 mb-3">
              Users with these roles are not checked against plan capabilities; access is RBAC-only. Use this for Platform Administrator, Tenant Administrator, or other roles that should always have full access regardless of subscription.
            </p>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {rolesForExempt.length === 0 ? (
                <p className="text-sm text-gray-500">No roles available.</p>
              ) : (
                rolesForExempt.map((r) => (
                  <label key={r.roleName} className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={exemptRoleNames.includes(r.roleName)}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setExemptRoleNames((prev) =>
                          checked
                            ? [...prev, r.roleName]
                            : prev.filter((n) => n !== r.roleName)
                        );
                      }}
                      className="rounded border-gray-300 text-[var(--color-primary-text)] focus:ring-[var(--color-primary-text)]"
                    />
                    <span className="text-sm text-gray-700">{r.displayName}</span>
                  </label>
                ))
              )}
            </div>
            <div className="mt-3">
              <button
                type="button"
                onClick={() => void handleSaveExemptRoles()}
                disabled={exemptRolesSaving}
                className="px-3 py-1.5 text-xs rounded-md bg-[var(--color-primary-text)] text-white hover:opacity-90 disabled:opacity-50"
              >
                {exemptRolesSaving ? 'Saving…' : 'Save exempt roles'}
              </button>
            </div>
          </div>

          {capabilitiesLoading ? (
            <p className="text-sm text-gray-600">Loading capabilities…</p>
          ) : capabilities.length === 0 ? (
            <p className="text-sm text-gray-600">
              No capabilities yet. Add one to group screens and attach them to plans.
            </p>
          ) : (
            <div className="border border-gray-200 rounded-md overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Code
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Display name
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Description
                    </th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">
                      Active
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {capabilities.map((c) => (
                    <tr key={c.id}>
                      <td className="px-4 py-2 text-sm font-mono text-gray-700">{c.code}</td>
                      <td className="px-4 py-2 text-sm text-gray-900">{c.displayName}</td>
                      <td className="px-4 py-2 text-sm text-gray-600 max-w-xs truncate">
                        {c.description ?? '—'}
                      </td>
                      <td className="px-4 py-2 text-sm text-center">
                        <span
                          className={
                            c.isActive
                              ? 'inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700'
                              : 'inline-flex items-center rounded-full bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-600'
                          }
                        >
                          {c.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-sm text-right">
                        <button
                          type="button"
                          onClick={() => void openEditCapabilityModal(c)}
                          className="p-1.5 rounded text-gray-500 hover:text-[var(--color-primary-text)] hover:bg-[var(--color-hover-bg)]"
                          title="Edit capability"
                          aria-label="Edit capability"
                        >
                          <PencilSquareIcon className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        )}

        {activeTab === 'promotions' && (
        <>
        {/* Promotions */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Promotions</h2>
              <p className="text-sm text-gray-600 mt-1">
                Create coupons and link them to plans. Supports percent or amount off; duration once, repeating, or forever (aligned with Stripe).
              </p>
            </div>
            <button
              type="button"
              onClick={openNewPromotionModal}
              className="px-3 py-1.5 text-xs rounded-md bg-[var(--color-primary-text)] text-white hover:opacity-90"
            >
              Add promotion
            </button>
          </div>
          {promotionsLoading ? (
            <p className="text-sm text-gray-600">Loading promotions…</p>
          ) : promotions.length === 0 ? (
            <p className="text-sm text-gray-600">No promotions yet. Add one to offer discounts on plans.</p>
          ) : (
            <div className="border border-gray-200 rounded-md overflow-hidden">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Code</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Discount</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Active</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {promotions.map((promo) => (
                    <tr key={promo.id}>
                      <td className="px-4 py-2 text-sm font-mono text-gray-900">{promo.code}</td>
                      <td className="px-4 py-2 text-sm text-gray-900">{promo.name}</td>
                      <td className="px-4 py-2 text-sm text-gray-700">
                        {promo.discountType === 'percent' ? `${promo.discountValue}%` : `$${promo.discountValue}`}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-700 capitalize">
                        {promo.durationType}
                        {promo.durationInCycles != null ? ` (${promo.durationInCycles})` : ''}
                      </td>
                      <td className="px-4 py-2 text-sm text-center">
                        <span
                          className={
                            promo.isActive
                              ? 'inline-flex items-center rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700'
                              : 'inline-flex items-center rounded-full bg-gray-50 px-2 py-0.5 text-xs font-medium text-gray-600'
                          }
                        >
                          {promo.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-4 py-2 text-sm text-right">
                        <button
                          type="button"
                          onClick={() => void openEditPromotionModal(promo)}
                          className="p-1.5 rounded text-gray-500 hover:text-[var(--color-primary-text)] hover:bg-[var(--color-hover-bg)]"
                          title="Edit promotion"
                          aria-label="Edit promotion"
                        >
                          <PencilSquareIcon className="h-5 w-5" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        </>
        )}

        {activeTab === 'plansPrices' && (
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-start justify-between gap-4 mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                Billing plans (TMOS)
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Define subscription plans linked to AI tiers. Each plan creates one product with two prices (monthly + annual).
              </p>
            </div>
            <button
              type="button"
              onClick={openNewPlanModal}
              className="px-3 py-1.5 text-xs rounded-md bg-[var(--color-primary-text)] text-white hover:opacity-90"
            >
              Add plan
            </button>
          </div>

          {plansLoading ? (
            <p className="text-sm text-gray-600">Loading plans…</p>
          ) : plans.length === 0 ? (
            <p className="text-sm text-gray-600">
              No billing plans have been created yet. Use &quot;Add plan&quot; to create your first
              subscription plan.
            </p>
          ) : (
            <div className="space-y-10">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Active plans &amp; prices</h3>
                <p className="text-xs text-gray-600 mt-1">
                  Includes active, paused, and hidden.
                </p>
                <div className="mt-4">
                  {activePlanGroups.length === 0 ? (
                    <p className="text-sm text-gray-600">No active plans.</p>
                  ) : (
                    renderPlanGroups(activePlanGroups, { section: 'active' })
                  )}
                </div>
              </div>

              <div>
                <h3 className="text-sm font-semibold text-gray-900">Retired plans &amp; prices</h3>
                <p className="text-xs text-gray-600 mt-1">
                  Includes retired, archived, and deleted.
                </p>
                <div className="mt-4">
                  {retiredPlanGroups.length === 0 ? (
                    <p className="text-sm text-gray-600">No retired plans.</p>
                  ) : (
                    renderPlanGroups(retiredPlanGroups, { section: 'retired' })
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        )}
      </div>

      {showProviderModal && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {providers.some(
                (p) =>
                  p.providerKey === form.providerKey &&
                  p.environment === form.environment
              )
                ? 'Edit billing provider'
                : 'Add billing provider'}
            </h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Provider key
                  </label>
                  <input
                    type="text"
                    value={form.providerKey}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, providerKey: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="stripe, adyen, braintree…"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    TMOS treats <code>provider_key + environment</code> as a unique pair.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Environment
                  </label>
                  <select
                    value={form.environment}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, environment: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="test">Test</option>
                    <option value="live">Live</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Use <span className="font-semibold">Test</span> while integrating and switch to{' '}
                    <span className="font-semibold">Live</span> for production.
                  </p>
                </div>
              </div>

              <div>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, isActive: e.target.checked }))
                    }
                    className="rounded border-gray-300 text-[var(--color-primary-text)] focus:ring-[var(--color-primary-text)]"
                  />
                  <span className="text-sm text-gray-700">
                    Use this provider ({form.providerKey} / {form.environment}) as the active billing provider
                  </span>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Publishable key
                </label>
                <input
                  type="text"
                  value={form.publishableKey}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, publishableKey: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="pk_test_..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Secret key
                </label>
                <input
                  type="password"
                  value={form.secretKey}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, secretKey: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="sk_test_..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Leave as <code>••••••••</code> to keep the existing key.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Webhook signing secret
                </label>
                <input
                  type="password"
                  value={form.webhookSecret}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, webhookSecret: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="whsec_..."
                />
                <p className="text-xs text-gray-500 mt-1">
                  Leave as <code>••••••••</code> to keep the existing secret.
                </p>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowProviderModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-[var(--color-hover-bg)]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  const ok = await handleSave();
                  if (ok) {
                    setShowProviderModal(false);
                  }
                }}
                disabled={saving}
                className="px-4 py-2 bg-[var(--color-primary-text)] text-white rounded-md hover:opacity-90 disabled:opacity-50"
              >
                {saving ? 'Saving…' : 'Save provider'}
              </button>
            </div>
          </div>
        </div>
      )}

      {planModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {planForm.idMonthly || planForm.idAnnual ? 'Edit billing plan' : 'Add billing plan'}
            </h3>
            <div className="space-y-4">
              {/* Row 1: Display Name | AI Tier */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display name
                  </label>
                  <input
                    type="text"
                    value={planForm.name}
                    onChange={(e) =>
                      setPlanForm((f) => ({
                        ...f,
                        name: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="Pro"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    AI tier
                  </label>
                  <select
                    value={planForm.aiTierId}
                    onChange={(e) =>
                      setPlanForm((f) => ({
                        ...f,
                        aiTierId: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="">Select a tier…</option>
                    {tiers.map((tier) => (
                      <option key={tier.id} value={tier.id}>
                        {tier.name} ({tier.dailyTokenCap.toLocaleString()} tokens/day)
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Row 2: Subtitle (optional) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Subtitle (optional)
                  </label>
                  <input
                    type="text"
                    value={planForm.subtitle}
                    onChange={(e) =>
                      setPlanForm((f) => ({
                        ...f,
                        subtitle: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="Short line under plan name on public pricing page"
                    maxLength={512}
                  />
                </div>
              </div>

              {/* Row 3: Currency */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Currency
                  </label>
                  <input
                    type="text"
                    value={planForm.currency}
                    onChange={(e) =>
                      setPlanForm((f) => ({
                        ...f,
                        currency: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md uppercase"
                    maxLength={8}
                    placeholder="usd"
                  />
                </div>
              </div>

              {/* Row 3: Price per Month | Price per Year */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price per month
                  </label>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={planForm.monthlyPriceUsd}
                    onChange={(e) =>
                      setPlanForm((f) => ({
                        ...f,
                        monthlyPriceUsd: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="e.g. 49.00 (leave empty to skip monthly)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price per year
                  </label>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={planForm.annualPriceUsd}
                    onChange={(e) =>
                      setPlanForm((f) => ({
                        ...f,
                        annualPriceUsd: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="e.g. 499.00 (leave empty to skip annual)"
                  />
                </div>
              </div>

              {/* Row 4: Plan code (optional, last) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Plan code (optional)
                  </label>
                  <input
                    type="text"
                    value={planForm.planCode}
                    onChange={(e) =>
                      setPlanForm((f) => ({
                        ...f,
                        planCode: e.target.value,
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="Leave empty to auto-generate from name"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Included capabilities
                </label>
                <p className="text-xs text-gray-500 mb-2">
                  Select which capabilities (entitlements) this plan includes. Tenants on this plan will have access to the screens attached to these capabilities (subject to RBAC).
                </p>
                <div className="border border-gray-200 rounded-md p-3 max-h-40 overflow-y-auto space-y-2">
                  {capabilities.length === 0 ? (
                    <p className="text-sm text-gray-500">No capabilities defined. Add capabilities above first.</p>
                  ) : (
                    capabilities.map((c) => (
                      <label key={c.id} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={planForm.selectedCapabilityIds.includes(c.id)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setPlanForm((f) => ({
                              ...f,
                              selectedCapabilityIds: checked
                                ? [...f.selectedCapabilityIds, c.id]
                                : f.selectedCapabilityIds.filter((id) => id !== c.id),
                            }));
                          }}
                          className="rounded border-gray-300 text-[var(--color-primary-text)] focus:ring-[var(--color-primary-text)]"
                        />
                        <span className="text-sm text-gray-700">
                          {c.displayName}
                          <span className="text-gray-500 font-mono ml-1">({c.code})</span>
                        </span>
                      </label>
                    ))
                  )}
                </div>
              </div>

              <div>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={planForm.isActive}
                    onChange={(e) =>
                      setPlanForm((f) => ({
                        ...f,
                        isActive: e.target.checked,
                      }))
                    }
                    className="rounded border-gray-300 text-[var(--color-primary-text)] focus:ring-[var(--color-primary-text)]"
                  />
                  <span className="text-sm text-gray-700">
                    Plan is active and available for new subscriptions
                  </span>
                </label>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPlanModalOpen(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-[var(--color-hover-bg)]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  const ok = await handleSavePlan();
                  if (ok) {
                    setPlanModalOpen(false);
                  }
                }}
                disabled={planSaving}
                className="px-4 py-2 bg-[var(--color-primary-text)] text-white rounded-md hover:opacity-90 disabled:opacity-50"
              >
                {planSaving ? 'Saving…' : 'Save plan'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Editing is handled via the plan modal (product + prices). */}

      {promotionModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {promotionForm.id ? 'Edit promotion' : 'Add promotion'}
            </h3>
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                  <input
                    type="text"
                    value={promotionForm.code}
                    onChange={(e) => setPromotionForm((f) => ({ ...f, code: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono"
                    placeholder="LAUNCH50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={promotionForm.name}
                    onChange={(e) => setPromotionForm((f) => ({ ...f, name: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="Launch 50% off"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                <input
                  type="text"
                  value={promotionForm.description}
                  onChange={(e) => setPromotionForm((f) => ({ ...f, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="First 3 months"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Discount type</label>
                  <select
                    value={promotionForm.discountType}
                    onChange={(e) =>
                      setPromotionForm((f) => ({ ...f, discountType: e.target.value as 'percent' | 'amount' }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="percent">Percent</option>
                    <option value="amount">Amount (fixed)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {promotionForm.discountType === 'percent' ? 'Percent off' : 'Amount off (USD)'}
                  </label>
                  <input
                    type="number"
                    min={0}
                    step={promotionForm.discountType === 'percent' ? 1 : 0.01}
                    value={promotionForm.discountValue}
                    onChange={(e) => setPromotionForm((f) => ({ ...f, discountValue: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder={promotionForm.discountType === 'percent' ? '50' : '10.00'}
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
                  <select
                    value={promotionForm.durationType}
                    onChange={(e) =>
                      setPromotionForm((f) => ({
                        ...f,
                        durationType: e.target.value as 'once' | 'repeating' | 'forever',
                      }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="once">Once</option>
                    <option value="repeating">Repeating</option>
                    <option value="forever">Forever</option>
                  </select>
                </div>
                {promotionForm.durationType === 'repeating' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Number of cycles</label>
                    <input
                      type="number"
                      min={1}
                      value={promotionForm.durationInCycles}
                      onChange={(e) => setPromotionForm((f) => ({ ...f, durationInCycles: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      placeholder="3"
                    />
                  </div>
                )}
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Starts at (optional)</label>
                  <input
                    type="datetime-local"
                    value={promotionForm.startsAt}
                    onChange={(e) => setPromotionForm((f) => ({ ...f, startsAt: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Ends at (optional)</label>
                  <input
                    type="datetime-local"
                    value={promotionForm.endsAt}
                    onChange={(e) => setPromotionForm((f) => ({ ...f, endsAt: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Apply to plans</label>
                <p className="text-xs text-gray-500 mb-2">
                  Select which billing plans this promotion can be used with.
                </p>
                <div className="border border-gray-200 rounded-md p-3 max-h-40 overflow-y-auto space-y-2">
                  {plans.length === 0 ? (
                    <p className="text-sm text-gray-500">No plans defined. Create plans first.</p>
                  ) : (
                    plans.map((p) => (
                      <label key={p.id} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={promotionForm.selectedPlanIds.includes(p.id)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setPromotionForm((f) => ({
                              ...f,
                              selectedPlanIds: checked
                                ? [...f.selectedPlanIds, p.id]
                                : f.selectedPlanIds.filter((id) => id !== p.id),
                            }));
                          }}
                          className="rounded border-gray-300 text-[var(--color-primary-text)] focus:ring-[var(--color-primary-text)]"
                        />
                        <span className="text-sm text-gray-700">
                          {p.name} — {p.billingPeriod} ${p.priceUsd.toFixed(2)}
                        </span>
                      </label>
                    ))
                  )}
                </div>
              </div>
              <div>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={promotionForm.isActive}
                    onChange={(e) =>
                      setPromotionForm((f) => ({ ...f, isActive: e.target.checked }))
                    }
                    className="rounded border-gray-300 text-[var(--color-primary-text)] focus:ring-[var(--color-primary-text)]"
                  />
                  <span className="text-sm text-gray-700">Promotion is active</span>
                </label>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPromotionModalOpen(false)}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-[var(--color-hover-bg)]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={async () => {
                  const ok = await handleSavePromotion();
                  if (ok) setPromotionModalOpen(false);
                }}
                disabled={promotionSaving}
                className="px-4 py-2 bg-[var(--color-primary-text)] text-white rounded-md hover:opacity-90 disabled:opacity-50"
              >
                {promotionSaving ? 'Saving…' : 'Save promotion'}
              </button>
            </div>
          </div>
        </div>
      )}

      {capabilityModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {capabilityForm.id ? 'Edit capability' : 'Add capability'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Code</label>
                <input
                  type="text"
                  value={capabilityForm.code}
                  onChange={(e) =>
                    setCapabilityForm((f) => ({ ...f, code: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md font-mono"
                  placeholder="e.g. alignment-core"
                  disabled={!!capabilityForm.id}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Lowercase, no spaces. Cannot be changed after create.
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Display name</label>
                <input
                  type="text"
                  value={capabilityForm.displayName}
                  onChange={(e) =>
                    setCapabilityForm((f) => ({ ...f, displayName: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="Alignment core"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description (optional)</label>
                <textarea
                  value={capabilityForm.description}
                  onChange={(e) =>
                    setCapabilityForm((f) => ({ ...f, description: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  rows={2}
                  placeholder="What this capability includes"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Screens included</label>
                <p className="text-xs text-gray-500 mb-2">
                  Select navigation screens that this capability entitles. Tenants with a plan that includes this capability can see these screens (if their role permits).
                </p>
                <input
                  type="text"
                  value={capabilityScreenSearch}
                  onChange={(e) => setCapabilityScreenSearch(e.target.value)}
                  placeholder="Search screens by name or code…"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm mb-2"
                  aria-label="Search screens"
                />
                <div className="border border-gray-200 rounded-md p-3 max-h-48 overflow-y-auto space-y-2">
                  {screens.length === 0 ? (
                    <p className="text-sm text-gray-500">No screens available.</p>
                  ) : (() => {
                    const q = capabilityScreenSearch.trim().toLowerCase();
                    const filtered = q
                      ? screens.filter(
                          (s) =>
                            s.displayName.toLowerCase().includes(q) ||
                            s.screenCode.toLowerCase().includes(q)
                        )
                      : screens;
                    return filtered.length === 0 ? (
                      <p className="text-sm text-gray-500">No screens match &quot;{capabilityScreenSearch}&quot;.</p>
                    ) : (
                      filtered.map((s) => (
                      <label key={s.id} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={capabilityForm.selectedScreenIds.includes(s.id)}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setCapabilityForm((f) => ({
                              ...f,
                              selectedScreenIds: checked
                                ? [...f.selectedScreenIds, s.id]
                                : f.selectedScreenIds.filter((id) => id !== s.id),
                            }));
                          }}
                          className="rounded border-gray-300 text-[var(--color-primary-text)] focus:ring-[var(--color-primary-text)]"
                        />
                        <span className="text-sm text-gray-700">
                          {s.displayName}
                          <span className="text-gray-500 font-mono ml-1">({s.screenCode})</span>
                        </span>
                      </label>
                    ))
                    );
                  })()}
                </div>
              </div>
              <div>
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={capabilityForm.isActive}
                    onChange={(e) =>
                      setCapabilityForm((f) => ({ ...f, isActive: e.target.checked }))
                    }
                    className="rounded border-gray-300 text-[var(--color-primary-text)] focus:ring-[var(--color-primary-text)]"
                  />
                  <span className="text-sm text-gray-700">Capability is active</span>
                </label>
              </div>
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => { setCapabilityModalOpen(false); setCapabilityScreenSearch(''); }}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-[var(--color-hover-bg)]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void handleSaveCapability()}
                disabled={capabilitySaving}
                className="px-4 py-2 bg-[var(--color-primary-text)] text-white rounded-md hover:opacity-90 disabled:opacity-50"
              >
                {capabilitySaving ? 'Saving…' : 'Save capability'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminBillingPage;

